import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import SortableHeader from '../components/SortableHeader';
import ConfirmDialog from '../components/ConfirmDialog';

// linkedCognitoSubs holds the Cognito user IDs to delete on profile cleanup.
// Older profiles may only have linkedProviders ({provider, sub} objects) — fallback to those.
function getLinkedSubs(profile) {
  if (Array.isArray(profile.linkedCognitoSubs)) return profile.linkedCognitoSubs.filter(Boolean);
  if (Array.isArray(profile.linkedProviders)) {
    return profile.linkedProviders
      .map(p => (typeof p === 'object' && p ? p.sub : null))
      .filter(Boolean);
  }
  return [];
}

// Sort value extractor — null/undefined are treated as missing and pushed to the end.
function getSortValue(p, key) {
  if (key === 'bookingCount') return p.bookingCount || 0;
  if (key === 'linkedProviders') return (p.linkedProviders || []).length;
  if (key === 'phoneVerifiedAt') return p.phoneVerifiedAt || null;
  if (key === 'lastActiveAt') return p.lastActiveAt || null;
  return p[key] || null;
}

// Numeric vs string compare; missing values always sink regardless of direction.
function compareProfiles(a, b, key) {
  const av = getSortValue(a, key);
  const bv = getSortValue(b, key);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv), 'ko-KR');
}

export default function AdminProfilesPage() {
  const { getIdToken } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('lastActiveAt');
  const [sortDir, setSortDir] = useState('desc');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingPurge, setPendingPurge] = useState(null);
  const [purgeBookings, setPurgeBookings] = useState(false);
  const [busy, setBusy] = useState(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) { setError('로그인이 필요합니다'); setLoading(false); return; }
      const res = await fetch(`${AWS_CONFIG.apiUrl}/profiles`, {
        headers: { Authorization: token },
      });
      if (!res.ok) {
        setError(`조회 실패 (${res.status})`);
      } else {
        const data = await res.json();
        setProfiles(data.profiles || []);
      }
    } catch (err) {
      setError(`네트워크: ${err.message}`);
    }
    setLoading(false);
  }, [getIdToken]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  async function handleDelete(profile) {
    const subs = getLinkedSubs(profile);
    if (subs.length === 0) {
      alert('연결된 Cognito 계정이 없습니다. UserProfiles 정리는 별도로 진행해주세요.');
      setPendingDelete(null);
      return;
    }
    setBusy(profile.phoneNumber);
    try {
      const token = await getIdToken();
      const results = await Promise.allSettled(subs.map(sub =>
        fetch(`${AWS_CONFIG.apiUrl}/admin/users/${sub}`, {
          method: 'DELETE',
          headers: { Authorization: token },
        }).then(async (res) => ({ ok: res.ok, status: res.status, body: await res.json().catch(() => ({})) }))
      ));
      const failed = results.filter(r => r.status === 'rejected' || (r.value && !r.value.ok));
      if (failed.length > 0) {
        const reasons = failed.map(f => f.value?.body?.error || f.reason?.message || 'unknown').join(', ');
        alert(`${subs.length}개 중 ${failed.length}개 Cognito 삭제 실패: ${reasons}`);
      } else {
        alert(`${subs.length}개 Cognito 계정 삭제 완료. UserProfiles 행은 보존됩니다.`);
      }
      await fetchProfiles();
    } catch (err) {
      alert(`네트워크 오류: ${err.message}`);
    }
    setBusy(null);
    setPendingDelete(null);
  }

  async function handlePurge(profile) {
    setBusy(profile.phoneNumber);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/admin/profiles/delete`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: profile.phoneNumber, deleteBookings: purgeBookings }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`완전 삭제 실패: ${body.error || res.status}`);
      } else {
        const cog = (body.cognitoResults || []).filter(r => r.deleted).length;
        const bkn = body.bookings?.deleted ?? 0;
        alert(`완전 삭제 완료\n• 프로필 행: 1개\n• Cognito 계정: ${cog}개\n• 예약 이력: ${purgeBookings ? `${bkn}개` : '보존'}`);
      }
      await fetchProfiles();
    } catch (err) {
      alert(`네트워크 오류: ${err.message}`);
    }
    setBusy(null);
    setPendingPurge(null);
    setPurgeBookings(false);
  }

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filtered = profiles.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.phoneNumber || '').includes(q)
      || (p.name || '').toLowerCase().includes(q)
      || (p.email || '').toLowerCase().includes(q);
  });

  const sortedProfiles = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const cmp = compareProfiles(a, b, sortKey);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  return (
    <div style={{ padding: 20 }}>
      <h1>고객 프로필</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 12 }}>
        💡 컬럼 헤더를 클릭하면 해당 항목으로 정렬됩니다. 같은 헤더를 다시 클릭하면 오름/내림차순이 바뀝니다.
      </p>
      <input
        className="input"
        placeholder="전화번호·이름·이메일 검색"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 360, marginBottom: 16 }}
      />

      {loading && <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>}
      {error && <p style={{ color: '#ff5252' }}>{error}</p>}

      {!loading && !error && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <SortableHeader label="전화번호"   sortKey="phoneNumber"      currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="이름"       sortKey="name"             currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="이메일"     sortKey="email"            currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="인증"       sortKey="phoneVerifiedAt"  currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="연결"       sortKey="linkedProviders"  currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="예약수"     sortKey="bookingCount"     currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                <SortableHeader label="최근 활동"  sortKey="lastActiveAt"     currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>작업</th>
              </tr>
            </thead>
            <tbody>
              {sortedProfiles.map(p => (
                <tr key={p.phoneNumber} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{p.phoneNumber}</td>
                  <td style={{ padding: '8px 10px' }}>{p.name || '-'}</td>
                  <td style={{ padding: '8px 10px' }}>{p.email || '-'}</td>
                  <td style={{ padding: '8px 10px' }}>{p.phoneVerifiedAt ? '✅' : '미검증'}</td>
                  <td style={{ padding: '8px 10px' }}>{(p.linkedProviders || []).join(', ') || '-'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{p.bookingCount || 0}</td>
                  <td style={{ padding: '8px 10px', fontSize: '0.8rem' }}>{p.lastActiveAt?.slice(0,10) || '-'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => setPendingDelete(p)}
                      disabled={busy === p.phoneNumber}
                      title="Cognito 계정만 삭제 (프로필·예약 보존)"
                      style={{
                        padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer',
                        border: 'none', borderRadius: 4, marginRight: 4,
                        background: '#fce4ec', color: '#ad1457',
                        opacity: busy === p.phoneNumber ? 0.5 : 1,
                      }}
                    >
                      {busy === p.phoneNumber ? '...' : '🗑 Cognito'}
                    </button>
                    <button
                      onClick={() => { setPurgeBookings(false); setPendingPurge(p); }}
                      disabled={busy === p.phoneNumber}
                      title="프로필 행 + Cognito 일괄 삭제 (예약은 선택)"
                      style={{
                        padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer',
                        border: 'none', borderRadius: 4,
                        background: '#ffebee', color: '#b71c1c', fontWeight: 600,
                        opacity: busy === p.phoneNumber ? 0.5 : 1,
                      }}
                    >
                      🔥 완전
                    </button>
                  </td>
                </tr>
              ))}
              {sortedProfiles.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
                  프로필이 없습니다
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        title="이 고객의 Cognito 계정을 삭제하시겠습니까?"
        message={pendingDelete ? (() => {
          const subs = getLinkedSubs(pendingDelete);
          const label = pendingDelete.name || pendingDelete.phoneNumber || '대상';
          return `${label} 고객의 연결된 Cognito 계정 ${subs.length}개를 일괄 삭제합니다.\n\n• 해당 사용자들은 즉시 로그인할 수 없게 됩니다\n• 고객 프로필(UserProfiles)·예약 이력(Bookings)은 보존됩니다\n• 되돌릴 수 없습니다`;
        })() : ''}
        confirmLabel="삭제"
        cancelLabel="취소"
        danger={true}
        requireTypedConfirmation={pendingDelete?.name || pendingDelete?.phoneNumber}
        onConfirm={() => handleDelete(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />
      <ConfirmDialog
        open={!!pendingPurge}
        title="이 고객 프로필을 완전히 삭제하시겠습니까?"
        message={pendingPurge ? (() => {
          const label = pendingPurge.name || pendingPurge.phoneNumber || '대상';
          const bk = pendingPurge.bookingCount || 0;
          return `${label} 고객의 프로필 행 + 연결된 Cognito 계정을 모두 삭제합니다.\n\n• UserProfiles 행 자체가 사라집니다\n• 되돌릴 수 없습니다\n• 현재 예약 이력: ${bk}건`;
        })() : ''}
        confirmLabel="완전 삭제"
        cancelLabel="취소"
        danger={true}
        requireTypedConfirmation={pendingPurge?.name || pendingPurge?.phoneNumber}
        onConfirm={() => handlePurge(pendingPurge)}
        onCancel={() => { setPendingPurge(null); setPurgeBookings(false); }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff3e0', borderRadius: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={purgeBookings}
            onChange={(e) => setPurgeBookings(e.target.checked)}
          />
          <span>예약 이력(Bookings)도 함께 삭제 — <strong style={{ color: '#b71c1c' }}>테스트 데이터에만</strong></span>
        </label>
      </ConfirmDialog>
    </div>
  );
}
